import { useMemo } from 'react';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';

import {
	ASANA,
	MONDAY,
	type SupportedThirdParty,
	TRELLO,
	JIRA_SOFTWARE,
	CLICK_UP,
	JIRA_BUSINESS,
} from '../constants/import-type';

import messages from './messages';

export const useContentForThirdParty = (thirdParty?: SupportedThirdParty) => {
	const { formatMessage } = useIntl();
	return useMemo(() => {
		switch (thirdParty) {
			case TRELLO:
				return {
					heading: formatMessage(messages.trelloHeading),
					description: formatMessage(messages.trelloDescription),
					entityScreenDescription: messages.trelloDescription,
					workspacesSelectorLabel: formatMessage(messages.trelloWorkspacesSelectorLabel),
					spaceSelectorLabel: '',
					spaceSelectorPlaceholder: '',
					workspacesSelectorPlaceholder: formatMessage(
						messages.trelloWorkspacesSelectorPlaceholder,
					),
					foldersSelectorLabel: '',
					foldersSelectorPlaceholder: '',
					errorOccurredFetchingFoldersHeader: '',
					subfoldersSelectorLabel: '',
					subfoldersSelectorPlaceholder: '',
					entitySelectorLabel: formatMessage(messages.trelloBoardsSelectorLabel),
					entitySelectorPlaceholder: formatMessage(messages.trelloBoardsSelectorPlaceholder),
					starredWorkspacesLabel: formatMessage(messages.trelloStarredWorkspacesLabel),
					otherWorkspacesLabel: formatMessage(messages.trelloOtherWorkspacesLabel),
					starredEntitiesLabel: formatMessage(messages.trelloStarredBoardsLabel),
					otherEntitiesLabel: formatMessage(messages.trelloOtherBoardsLabel),
					statsHeading: formatMessage(messages.trelloStatsHeading),
					memberStats: messages.memberStats,
					cardStats: messages.cardStats,
					cardStatsShort: messages.cardStatsShort,
					usersStatsShort: messages.usersStatsShort,
					importStep1Description: formatMessage(messages.trelloImportStep1Description),
					noWorkspacesFoundHeader: formatMessage(messages.trelloNoWorkspacesFoundHeader),
					noWorkspacesFoundDescription: formatMessage(messages.trelloNoWorkspacesFoundDescription),
					errorOccurredFetchingWorkspacesHeader: formatMessage(
						messages.trelloErrorOccurredFetchingWorkspacesHeader,
					),
					errorOccurredFetchingSpacesHeader: '',
					noSpacesFoundHeader: '',
					noSpacesFoundDescription: '',
					noEntitiesFoundHeader: formatMessage(messages.trelloNoEntitiesFoundHeader),
					noEntitiesFoundDescription: formatMessage(messages.trelloNoEntitiesFoundDescription),
					errorOccurredFetchingEntitiesHeader: formatMessage(
						messages.trelloErrorOccurredFetchingEntitiesHeader,
					),
					noAccountErrorHeader: formatMessage(messages.trelloNoAccountErrorHeader),
					searchEntitiesErrorOccurredDescription: '',
					loadingMoreEntitiesErrorOccurredDescription: '',
				};
			case MONDAY:
				return {
					heading: formatMessage(messages.mondayHeading),
					description: formatMessage(messages.mondayDescription),
					entityScreenDescription: messages.mondayDescription,
					workspacesSelectorLabel: formatMessage(messages.mondayWorkspacesSelectorLabel),
					workspacesSelectorPlaceholder: formatMessage(
						messages.mondayWorkspacesSelectorPlaceholder,
					),
					spaceSelectorLabel: '',
					spaceSelectorPlaceholder: '',
					foldersSelectorLabel: formatMessage(messages.mondayFoldersSelectorLabel),
					foldersSelectorPlaceholder: formatMessage(messages.mondayFoldersSelectorPlaceholder),
					errorOccurredFetchingFoldersHeader: formatMessage(
						messages.mondayErrorOccurredFetchingFoldersHeader,
					),
					subfoldersSelectorLabel: formatMessage(messages.mondaySubfoldersSelectorLabel),
					subfoldersSelectorPlaceholder: formatMessage(
						messages.mondaySubfoldersSelectorPlaceholder,
					),
					entitySelectorLabel: formatMessage(messages.mondayProjectsSelectorLabel),
					entitySelectorPlaceholder: formatMessage(messages.mondayProjectsSelectorPlaceholder),
					starredWorkspacesLabel: formatMessage(messages.mondayStarredWorkspacesLabel),
					otherWorkspacesLabel: formatMessage(messages.mondayOtherWorkspacesLabel),
					starredEntitiesLabel: formatMessage(messages.mondayStarredProjectsLabel),
					otherEntitiesLabel: formatMessage(messages.mondayOtherProjectsLabel),
					statsHeading: formatMessage(messages.mondayStatsHeading),
					memberStats: messages.memberStats,
					cardStats: messages.mondayCardStats,
					cardStatsShort: messages.mondayCardStatsShort,
					usersStatsShort: messages.mondayUsersStatsShort,
					importStep1Description: formatMessage(messages.mondayImportStep1Description),
					noWorkspacesFoundHeader: formatMessage(messages.mondayNoWorkspacesFoundHeader),
					noWorkspacesFoundDescription: formatMessage(messages.mondayNoWorkspacesFoundDescription),
					errorOccurredFetchingWorkspacesHeader: formatMessage(
						messages.mondayErrorOccurredFetchingWorkspacesHeader,
					),
					errorOccurredFetchingSpacesHeader: '',
					noSpacesFoundHeader: '',
					noSpacesFoundDescription: '',
					noEntitiesFoundHeader: formatMessage(messages.mondayNoEntitiesFoundHeader),
					noEntitiesFoundDescription: formatMessage(messages.mondayNoEntitiesFoundDescription),
					noEntitiesFoundWithFolderDescription: formatMessage(
						messages.mondayNoEntitiesFoundWithFolderDescription,
					),
					noEntitiesFoundWithSubfolderDescription: formatMessage(
						messages.mondayNoEntitiesFoundWithSubfolderDescription,
					),
					errorOccurredFetchingEntitiesHeader: formatMessage(
						messages.mondayErrorOccurredFetchingEntitiesHeader,
					),
					searchEntitiesErrorOccurredDescription: '',
					loadingMoreEntitiesErrorOccurredDescription: '',
					noAccountErrorHeader: formatMessage(messages.mondayNoAccountErrorHeader),
					reviewScreenDescription: messages.mondayReviewScreenDescriptionSpork,
					fieldsTileLoadingDescription: formatMessage(
						messages.mondayFieldsTileLoadingDescriptionSpork,
					),
					fieldsMappingPageDescription: messages.mondayFieldsMappingPageDescriptionSpork,
					disabledFieldTooltip: formatMessage(messages.mondayDisabledFieldTooltip),
					hierarchyTileDescription: formatMessage(messages.mondayHierarchyTileDescriptionSpork),
					hierarchyMappingTileDescription: messages.mondayHierarchyMappingTileDescriptionSpork,
					hierarchyTileLoadingDescription: formatMessage(
						messages.mondayHierarchyTileLoadingDescriptionSpork,
					),
					hierarchyPageDescription: messages.mondayHierarchyPageDescriptionSpork,
					hierarchyMappingDescription: messages.mondayHierarchyMappingDescriptionSpork,
					hierarchyStatusToolTip: formatMessage(messages.mondayHierarchyStatusToolTip),
					hierarchyCategoryToolTip: formatMessage(messages.mondayHierarchyCategoryToolTip),
					hierarchyEpicToolTip: formatMessage(messages.mondayHierarchyEpicToolTip),
					usersTileHeading: formatMessage(messages.mondayUsersTileHeading),
					usersTileDescription: '',
					usersTileDescriptionUnchecked: formatMessage(
						messages.mondayUsersTileDescriptionUnchecked,
					),
					usersTileWarningDescription: formatMessage(messages.usersTileWarningDescription),
					preOAuthModalTitle: formatMessage(messages.mondayPreOAuthModalTitle),
					preOAuthModalDescription: messages.mondayPreOAuthModalDescription,
					oAuthUnsuccessFlagTitle: formatMessage(messages.mondayOAuthUnsuccessFlagTitle),
					oAuthUnsuccessFlagDescription: messages.mondayOAuthUnsuccessFlagDescription,
					addAccessTokenModalDescription: messages.mondayAddAccessTokenModalDescriptionSpork,
					editAccessTokenModalDescription: messages.mondayEditAccessTokenModalDescription,
					invalidAccessTokenModalDescription: messages.mondayInvalidAccessTokenModalDescription,
					changeCurrentAccount: messages.mondayChangeCurrentAccount,
					loadingSiteNameErrorOccurredDescription:
						messages.mondayLoadingSiteNameErrorOccurredDescription,
				};
			case ASANA:
				return {
					heading: formatMessage(messages.asanaHeading),
					description: formatMessage(messages.asanaDescription),
					entityScreenDescription: messages.asanaDescription,
					workspacesSelectorLabel: formatMessage(messages.asanaWorkspacesSelectorLabel),
					workspacesSelectorPlaceholder: formatMessage(messages.asanaWorkspacesSelectorPlaceholder),
					spaceSelectorLabel: '',
					spaceSelectorPlaceholder: '',
					foldersSelectorLabel: '',
					foldersSelectorPlaceholder: '',
					errorOccurredFetchingFoldersHeader: '',
					subfoldersSelectorLabel: '',
					subfoldersSelectorPlaceholder: '',
					entitySelectorLabel: formatMessage(messages.asanaProjectsSelectorLabel),
					entitySelectorPlaceholder: formatMessage(messages.asanaProjectsSelectorPlaceholder),
					starredWorkspacesLabel: formatMessage(messages.asanaStarredWorkspacesLabel),
					otherWorkspacesLabel: formatMessage(messages.asanaOtherWorkspacesLabel),
					starredEntitiesLabel: formatMessage(messages.asanaStarredProjectsLabel),
					otherEntitiesLabel: formatMessage(messages.asanaOtherProjectsLabel),
					statsHeading: formatMessage(messages.asanaStatsHeading),
					memberStats: messages.asanaMemberStats,
					cardStats: messages.asanaCardStats,
					cardStatsShort: messages.cardStatsShort,
					usersStatsShort: messages.usersStatsShort,
					importStep1Description: formatMessage(messages.asanaImportStep1Description),
					noWorkspacesFoundHeader: formatMessage(messages.asanaNoWorkspacesFoundHeader),
					noWorkspacesFoundDescription: formatMessage(messages.asanaNoWorkspacesFoundDescription),
					errorOccurredFetchingWorkspacesHeader: formatMessage(
						messages.asanaErrorOccurredFetchingWorkspacesHeader,
					),
					errorOccurredFetchingSpacesHeader: '',
					noSpacesFoundHeader: '',
					noSpacesFoundDescription: '',
					noEntitiesFoundHeader: formatMessage(messages.asanaNoEntitiesFoundHeader),
					noEntitiesFoundDescription: formatMessage(messages.asanaNoEntitiesFoundDescription),
					errorOccurredFetchingEntitiesHeader: formatMessage(
						messages.asanaErrorOccurredFetchingEntitiesHeader,
					),
					searchEntitiesErrorOccurredDescription: formatMessage(
						messages.asanaSearchEntitiesErrorOccurredDescription,
					),
					loadingMoreEntitiesErrorOccurredDescription: formatMessage(
						messages.asanaLoadingMoreEntitiesErrorOccurredDescription,
					),
					noAccountErrorHeader: formatMessage(messages.asanaNoAccountErrorHeader),
					reviewScreenDescription: messages.asanaReviewScreenDescriptionSpork,
					fieldsTileLoadingDescription: formatMessage(
						messages.asanaFieldsTileLoadingDescriptionSpork,
					),
					fieldsMappingPageDescription: messages.asanaFieldsMappingPageDescriptionSpork,
					hierarchyTileDescription: formatMessage(messages.asanaHierarchyTileDescription),
					hierarchyTileLoadingDescription: formatMessage(
						messages.asanaHierarchyTileLoadingDescription,
					),
					hierarchyMappingTileDescription: messages.asanaHierarchyMappingTileDescription,
					hierarchyPageDescription: messages.asanaHierarchyPageDescriptionSpork,
					hierarchyMappingDescription: messages.asanaHierarchyMappingDescriptionSpork,
					hierarchyStatusToolTip: formatMessage(messages.asanaHierarchyStatusToolTip),
					hierarchyCategoryToolTip: formatMessage(messages.asanaHierarchyCategoryToolTip),
					hierarchyEpicToolTip: formatMessage(messages.asanaHierarchyEpicToolTip),
					hierarchySubtaskToolTip: formatMessage(messages.asanaHierarchySubtaskToolTip),
					usersTileHeading: formatMessage(messages.asanaUsersTileHeading),
					usersTileDescription: '',
					usersTileDescriptionUnchecked: formatMessage(messages.asanaUsersTileDescriptionUnchecked),
					usersTileWarningDescription: formatMessage(messages.usersTileWarningDescription),
					disabledFieldTooltip: formatMessage(messages.asanaDisabledFieldTooltip),
					addAccessTokenModalDescription: messages.asanaAddAccessTokenModalDescriptionSpork,
					editAccessTokenModalDescription: messages.asanaEditAccessTokenModalDescription,
					invalidAccessTokenModalDescription: messages.asanaInvalidAccessTokenModalDescription,
					changeCurrentAccount: messages.asanaChangeCurrentAccount,
				};
			case JIRA_SOFTWARE:
				return {
					heading: formatMessage(messages.jiraHeading),
					description: formatMessage(messages.jiraDescriptionSpork),
					entityScreenDescription: messages.jiraDescriptionSpork,
					workspacesSelectorLabel: formatMessage(messages.jiraProjectSelectorLabel),
					workspacesSelectorPlaceholder: formatMessage(messages.jiraProjectSelectorPlaceholder),
					spaceSelectorLabel: '',
					spaceSelectorPlaceholder: '',
					foldersSelectorLabel: '',
					foldersSelectorPlaceholder: '',
					errorOccurredFetchingFoldersHeader: '',
					subfoldersSelectorLabel: '',
					subfoldersSelectorPlaceholder: '',
					entitySelectorLabel: formatMessage(messages.jiraProjectSelectorLabel),
					entitySelectorPlaceholder: formatMessage(messages.jiraProjectSelectorPlaceholder),
					starredWorkspacesLabel: '',
					otherWorkspacesLabel: '',
					starredEntitiesLabel: formatMessage(messages.jiraStarredProjectsLabel),
					otherEntitiesLabel: formatMessage(messages.jiraOtherProjectsLabel),
					statsHeading: formatMessage(messages.jiraStatsHeading),
					memberStats: messages.memberStats,
					cardStats: messages.cardStats,
					cardStatsShort: messages.cardStatsShort,
					usersStatsShort: messages.usersStatsShort,
					projectCreateHeading: formatMessage(messages.jiraProjectCreateHeading),
					reviewScreenDescription: messages.jiraReviewScreenDescriptionComplexspork,
					fieldsTileLoadingDescription: formatMessage(
						messages.jiraFieldsTileLoadingDescriptionSpork,
					),
					fieldsMappingPageDescription: messages.jiraFieldsMappingPageDescription,
					fieldsMappingPageIssueTypesMessage: formatMessage(
						messages.jiraFieldsMappingPageIssueTypesMessage,
					),
					fieldsTileInfoDescription: messages.jiraFieldsTileInfoDescription,
					usersTileHeading: formatMessage(messages.jiraUsersTileHeading),
					usersTileDescription: formatMessage(messages.jiraUserTileDescription),
					usersTileDescriptionUnchecked: formatMessage(messages.jiraUsersTileDescriptionUnchecked),
					usersTileWarningDescription: formatMessage(messages.usersTileWarningDescription),
					disabledFieldTooltip: formatMessage(messages.jiraDisabledFieldTooltip),
					importStep1Description: formatMessage(messages.jiraImportStep1Description),
					noWorkspacesFoundHeader: '',
					noWorkspacesFoundDescription: '',
					errorOccurredFetchingWorkspacesHeader: '',
					noEntitiesFoundHeader: formatMessage(messages.jiraNoEntitiesFoundHeader),
					noEntitiesFoundDescription: formatMessage(messages.jiraNoEntitiesFoundDescription),
					errorOccurredFetchingEntitiesHeader: formatMessage(
						messages.jiraErrorOccurredFetchingEntitiesHeader,
					),
					errorOccurredFetchingSpacesHeader: '',
					noSpacesFoundHeader: '',
					noSpacesFoundDescription: '',
					noAccountErrorHeader: '',
					searchEntitiesErrorOccurredDescription: '',
					loadingMoreEntitiesErrorOccurredDescription: '',
				};
			case CLICK_UP:
				return {
					heading: formatMessage(messages.clickupHeading),
					description: formatMessage(messages.clickupDescription),
					entityScreenDescription: messages.clickupDescription,
					workspacesSelectorLabel: formatMessage(messages.clickupWorkspaceSelectorLabel),
					workspacesSelectorPlaceholder: formatMessage(
						messages.clickupWorkspaceSelectorPlaceholder,
					),
					spaceSelectorLabel: formatMessage(messages.clickupSpaceSelector),
					spaceSelectorPlaceholder: formatMessage(messages.clickupSpaceSelectorPlaceholder),
					foldersSelectorLabel: formatMessage(messages.clickupFolderSelector),
					foldersSelectorPlaceholder: formatMessage(messages.clickupFolderSelectorPlaceholder),
					errorOccurredFetchingFoldersHeader: '',
					subfoldersSelectorLabel: '',
					subfoldersSelectorPlaceholder: '',
					entitySelectorLabel: formatMessage(messages.clickupListSelector),
					entitySelectorPlaceholder: formatMessage(messages.clickupListSelectorPlaceholder),
					starredWorkspacesLabel: '',
					otherWorkspacesLabel: '',
					starredEntitiesLabel: '',
					otherEntitiesLabel: '',
					statsHeading: formatMessage(messages.clickupStatsHeading),
					memberStats: messages.memberStats,
					cardStats: messages.clickupCardStats,
					cardStatsShort: messages.clickupCardStatsShort,
					usersStatsShort: messages.usersStatsShort,
					reviewScreenDescription: messages.clickupReviewScreenDescriptionSpork,
					fieldsTileLoadingDescription: formatMessage(
						messages.clickupFieldsTileLoadingDescriptionSpork,
					),
					fieldsMappingPageDescription: messages.clickupFieldsMappingPageDescription,
					fieldsMappingPageIssueTypesMessage: ff('x2c-clickup-importer-multi-list')
						? formatMessage(messages.clickupStatusMappingEntityName)
						: undefined,
					usersTileHeading: formatMessage(messages.clickupUsersTileHeading),
					usersTileDescription: formatMessage(messages.clickupUserTileDescription),
					usersTileDescriptionUnchecked: formatMessage(
						messages.clickupUsersTileDescriptionUnchecked,
					),
					usersTileWarningDescription: formatMessage(messages.usersTileWarningDescription),
					attachmentTileEnabledDescription: formatMessage(
						messages.clickupAttachmentTileEnabledDescription,
					),
					importStep1Description: ff('x2c-clickup-importer-multi-list')
						? formatMessage(messages.clickupImportStep1DescriptionMultiLists)
						: formatMessage(messages.clickupImportStep1Description),
					noWorkspacesFoundHeader: formatMessage(messages.clickupNoWorkspacesFoundHeader),
					noWorkspacesFoundDescription: formatMessage(messages.clickupNoWorkspacesFoundDescription),
					errorOccurredFetchingWorkspacesHeader: formatMessage(
						messages.clickupErrorOccurredFetchingWorkspacesHeader,
					),
					errorOccurredFetchingSpacesHeader: formatMessage(
						messages.clickupErrorOccurredFetchingSpacesHeader,
					),
					noSpacesFoundHeader: formatMessage(messages.clickupNoSpacesFoundHeader),
					noSpacesFoundDescription: formatMessage(messages.clickupNoSpacesFoundDescription),
					noEntitiesFoundHeader: formatMessage(messages.clickupNoEntitiesFoundHeader),
					noEntitiesFoundDescription: formatMessage(messages.clickupNoEntitiesFoundDescription),
					noEntitiesFoundWithFolderDescription: formatMessage(
						messages.clickupNoEntitiesFoundWithFolderDescription,
					),
					errorOccurredFetchingEntitiesHeader: formatMessage(
						messages.clickupErrorOccurredFetchingEntitiesHeader,
					),
					noAccountErrorHeader: '',
					searchEntitiesErrorOccurredDescription: '',
					loadingMoreEntitiesErrorOccurredDescription: '',
					hierarchyMappingTileDescription: messages.clickupHierarchyMappingTileDescriptionSpork,
					hierarchyTileLoadingDescription: formatMessage(
						messages.clickupHierarchyTileLoadingDescriptionSpork,
					),
					hierarchyMappingDescription: messages.clickupHierarchyMappingDescriptionSpork,
					hierarchyEpicToolTip: formatMessage(messages.clickupHierarchyEpicToolTip),
					hierarchySubtaskToolTip: formatMessage(messages.clickupHierarchySubtaskToolTip),
					statusMappingDescription: messages.clickupStatusMappingDescription,
					statusMappingEntityName: formatMessage(messages.clickupStatusMappingEntityName),
					rateLimitWarning: messages.clickupRateLimitWarning,
					cardStatsInfo: ff('x2c-clickup-importer-multi-list')
						? messages.clickupStatsInfoMultiList
						: messages.clickupStatsInfo,
					multiEntityInfo: messages.clickupMultiListInfo,
					oAuthUnsuccessFlagTitle: formatMessage(messages.clickupOAuthUnsuccessFlagTitle),
					oAuthUnsuccessFlagDescription: messages.clickupOAuthUnsuccessFlagDescription,
				};
			case JIRA_BUSINESS:
				return {
					heading: formatMessage(messages.jiraBusinessHeading),
					description: '',
					entityScreenDescription: messages.jiraBusinessDescription,
					workspacesSelectorLabel: formatMessage(messages.jiraBusinessProjectSelectorLabel),
					workspacesSelectorPlaceholder: formatMessage(messages.jiraProjectSelectorPlaceholder),
					spaceSelectorLabel: '',
					spaceSelectorPlaceholder: '',
					foldersSelectorLabel: '',
					foldersSelectorPlaceholder: '',
					errorOccurredFetchingFoldersHeader: '',
					subfoldersSelectorLabel: '',
					subfoldersSelectorPlaceholder: '',
					entitySelectorLabel: formatMessage(messages.jiraBusinessProjectSelectorLabel),
					entitySelectorPlaceholder: formatMessage(messages.jiraProjectSelectorPlaceholder),
					starredWorkspacesLabel: '',
					otherWorkspacesLabel: '',
					starredEntitiesLabel: formatMessage(messages.jiraStarredProjectsLabel),
					otherEntitiesLabel: formatMessage(messages.jiraOtherProjectsLabel),
					statsHeading: formatMessage(messages.jiraBusinessStatsHeading),
					memberStats: messages.memberStats,
					cardStats: messages.jiraBusinessCardStats,
					cardStatsShort: messages.cardStatsShort,
					usersStatsShort: messages.usersStatsShort,
					reviewScreenDescription: messages.jiraBusinessReviewScreenDescription,
					usersTileHeading: formatMessage(messages.jiraUsersTileHeading),
					usersTileDescription: formatMessage(messages.jiraBusinessUserTileDescription),
					usersTileDescriptionUnchecked: '',
					usersTileWarningDescription: formatMessage(messages.usersTileWarningDescription),
					fieldsTileLoadingDescription: formatMessage(
						messages.jiraBusinessFieldsTileLoadingDescription,
					),
					fieldsTileInfoDescription: messages.jiraBusinessFieldsTileInfoDescription,
					fieldsMappingPageDescription: messages.jiraBusinessFieldsMappingPageDescription,
					fieldsMappingPageIssueTypesMessage: formatMessage(
						messages.jiraFieldsMappingPageIssueTypesMessage,
					),
					disabledFieldTooltip: formatMessage(messages.jiraBusinessDisabledFieldTooltip),
					importStep1Description: formatMessage(messages.jiraBusinessImportStep1Description),
					noWorkspacesFoundHeader: '',
					noWorkspacesFoundDescription: '',
					noSpacesFoundHeader: '',
					noSpacesFoundDescription: '',
					errorOccurredFetchingWorkspacesHeader: '',
					errorOccurredFetchingSpacesHeader: '',
					noEntitiesFoundHeader: formatMessage(messages.jiraNoEntitiesFoundHeader),
					noEntitiesFoundDescription: formatMessage(messages.jiraNoEntitiesFoundDescription),
					errorOccurredFetchingEntitiesHeader: formatMessage(
						messages.jiraErrorOccurredFetchingEntitiesHeader,
					),
					searchEntitiesErrorOccurredDescription: '',
					loadingMoreEntitiesErrorOccurredDescription: '',
					noAccountErrorHeader: '',
				};
			default:
				return {
					heading: '',
					description: '',
					workspacesSelectorLabel: '',
					workspacesSelectorPlaceholder: '',
					spaceSelectorLabel: '',
					spaceSelectorPlaceholder: '',
					foldersSelectorLabel: '',
					foldersSelectorPlaceholder: '',
					errorOccurredFetchingFoldersHeader: '',
					subfoldersSelectorLabel: '',
					subfoldersSelectorPlaceholder: '',
					entitySelectorLabel: '',
					entitySelectorPlaceholder: '',
					starredWorkspacesLabel: '',
					otherWorkspacesLabel: '',
					starredEntitiesLabel: '',
					otherEntitiesLabel: '',
					statsHeading: '',
					memberStats: messages.memberStats,
					cardStats: messages.cardStats,
					cardStatsShort: messages.cardStatsShort,
					usersStatsShort: messages.usersStatsShort,
					importStep1Description: '',
					noWorkspacesFoundHeader: '',
					noWorkspacesFoundDescription: '',
					noSpacesFoundHeader: '',
					noSpacesFoundDescription: '',
					errorOccurredFetchingWorkspacesHeader: '',
					errorOccurredFetchingSpacesHeader: '',
					noEntitiesFoundHeader: '',
					noEntitiesFoundDescription: '',
					errorOccurredFetchingEntitiesHeader: '',
					searchEntitiesErrorOccurredDescription: '',
					loadingMoreEntitiesErrorOccurredDescription: '',
					noAccountErrorHeader: '',
				};
		}
	}, [formatMessage, thirdParty]);
};
